import m from 'mithril'
import { state } from '../state.ts'

const icon = (paths: string[]) =>
  m(
    'svg.icon',
    {
      viewBox: '0 0 24 24',
      width: 22,
      height: 22,
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.8,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
    },
    paths.map((d) => m('path', { d })),
  )

const ICONS = {
  back: ['M15 5l-7 7 7 7'],
  home: ['M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z'],
  search: ['M10.5 3a7.5 7.5 0 1 1 0 15 7.5 7.5 0 0 1 0-15z', 'M21 21l-5.4-5.4'],
  lists: ['M8.5 6.5h11.5', 'M8.5 12h11.5', 'M8.5 17.5h11.5', 'M4 6.5h.01', 'M4 12h.01', 'M4 17.5h.01'],
  user: ['M20 21a8 8 0 1 0-16 0', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
  settings: [
    'M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z',
    'M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.2A1.7 1.7 0 0 0 7.1 19.7l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 3.1 14H3a2 2 0 1 1 0-4h.2a1.7 1.7 0 0 0 1.2-2.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 10 3.1V3a2 2 0 1 1 4 0v.2a1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0 1.2 2.9H21a2 2 0 1 1 0 4h-.2a1.7 1.7 0 0 0-1.4 1z',
  ],
}

const tab = (href: string, label: string, paths: string[], active: boolean) =>
  m(
    m.route.Link,
    { href, class: active ? 'tab active' : 'tab', 'aria-current': active ? 'page' : undefined },
    [icon(paths), m('span.tab-label', label)],
  )

/** `action` is an icon link at the right of the header, like the settings gear on your profile. */
type Screen = { title: string; back?: string; action?: { href: string; label: string; icon: string[] } }

/** Tab roots get a title; detail screens get a back button that falls back to a sensible parent. */
const screenFor = (path: string, myProfile: string): Screen => {
  if (path === '/') return { title: 'Food Tracker' }
  if (path.startsWith('/search')) return { title: 'Search' }
  if (path === '/lists') return { title: 'Lists' }
  if (path.startsWith('/settings')) return { title: 'Settings', back: myProfile }
  if (path === '/login') return { title: 'Sign in' }
  if (path === myProfile) {
    return { title: 'Profile', action: { href: '/settings', label: 'Settings', icon: ICONS.settings } }
  }
  if (path.startsWith('/r/')) return { title: 'Restaurant', back: '/search' }
  if (path.startsWith('/l/')) return { title: 'List', back: '/lists' }
  if (path.startsWith('/u/')) return { title: 'Profile', back: '/' }
  return { title: 'Food Tracker', back: '/' }
}

const goBack = (fallback: string) => {
  // history.length counts the entry we're on, so 1 means the app was opened straight onto this page.
  if (window.history.length > 1) window.history.back()
  else m.route.set(fallback)
}

export const Layout: m.Component = {
  view: ({ children }) => {
    const path = m.route.get() || '/'
    const me = state.me
    const profileHref = me ? `/u/${me.username}` : '/login'
    const screen = screenFor(path, profileHref)

    return [
      m('header.topbar', [
        m('div.topbar-title', [
          screen.back
            ? m(
                'button.back',
                { type: 'button', 'aria-label': 'Back', onclick: () => goBack(screen.back!) },
                icon(ICONS.back),
              )
            : null,
          m('span.title', screen.title),
        ]),
        screen.action
          ? m(
              m.route.Link,
              { href: screen.action.href, class: 'topbar-action', 'aria-label': screen.action.label },
              icon(screen.action.icon),
            )
          : null,
        m('nav.nav', [
          tab('/', 'Home', ICONS.home, path === '/'),
          tab('/search', 'Search', ICONS.search, path.startsWith('/search')),
          tab('/lists', 'Lists', ICONS.lists, path === '/lists' || path.startsWith('/l/')),
          tab(
            profileHref,
            me ? 'Profile' : 'Sign in',
            ICONS.user,
            path === profileHref || path.startsWith('/settings'),
          ),
        ]),
      ]),
      m('main.container', children),
    ]
  },
}
